/** SUNTRONIX ONE — Integration Readiness Matrix.
 *  Administration → ONE CONNECT → Integration Readiness.
 *  Every external capability with declared access mode, honest status,
 *  simulation + manual fallback — nothing hidden, nothing bypassed. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PlugZap, FlaskConical, FileQuestion, Settings2 } from "lucide-react";

const STATUS_TONE = {
  LIVE: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "SETUP REQUIRED": "bg-amber-50 text-amber-800 border-amber-200",
  SIMULATION: "bg-sky-50 text-sky-700 border-sky-200",
  "MANUAL FALLBACK": "bg-indigo-50 text-indigo-700 border-indigo-200",
  DISABLED: "bg-slate-100 text-slate-500 border-slate-200",
};

const MODE_LABEL = {
  PUBLIC_NO_KEY: "PUBLIC · NO KEY",
  FREE_ACCOUNT_REQUIRED: "FREE ACCOUNT",
  OAUTH_REQUIRED: "OAUTH",
  PARTNER_AUTH_REQUIRED: "PARTNER AUTH",
  COMMERCIAL_CREDENTIAL_REQUIRED: "COMMERCIAL KEY",
};

export default function IntegrationReadiness() {
  const [rows, setRows] = useState(null);
  const [reqDialog, setReqDialog] = useState(null);
  const [simDialog, setSimDialog] = useState(null);
  const load = () => api.get("/one-connect/readiness")
    .then(({ data }) => setRows(data.items || [])).catch(() => toast.error("Could not load readiness matrix"));
  useEffect(() => { load(); }, []);

  const testConn = async (r) => {
    try {
      const { data } = await api.post(`/one-connect/readiness/${r.id}/test-connection`);
      data.ok ? toast.success(data.result) : toast.error(data.result);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };

  const cats = [...new Set((rows || []).map((r) => r.category))];

  return (
    <div className="p-6 max-w-[1400px]" data-testid="integration-readiness-page">
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-400">Administration · One Connect</div>
      <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-0.5">Integration Readiness</h1>
      <p className="text-[12px] text-slate-500 mt-1 max-w-2xl">
        Every external capability — its true access requirement, current status, and the legitimate
        fallback available today. No credential is ever bypassed; simulated runs are always labelled SIMULATED.
      </p>
      {!rows && <div className="mt-6 text-[12px] text-slate-400">Loading…</div>}
      {cats.map((cat) => (
        <div key={cat} className="mt-6">
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400 mb-2">{cat}</div>
          <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-[10px] uppercase text-slate-400 border-b border-slate-100">
                  <th className="px-3 py-2">Service</th>
                  <th className="px-3 py-2">Access mode</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Simulation</th>
                  <th className="px-3 py-2">Manual fallback</th>
                  <th className="px-3 py-2">Last connection</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {rows.filter((r) => r.category === cat).map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`readiness-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.service}
                      {r.status_note && <div className="text-[10px] text-amber-700">{r.status_note}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[9.5px] font-semibold text-slate-600">
                        {MODE_LABEL[r.access_mode] || r.access_mode}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full border px-2 py-0.5 text-[9.5px] font-semibold ${STATUS_TONE[r.configuration_status] || STATUS_TONE.DISABLED}`}
                        data-testid={`readiness-status-${r.id}`}>
                        {r.live_status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[11px]">{r.simulation_available ? "Available" : "—"}</td>
                    <td className="px-3 py-2 text-[11px]" title={r.fallback_note || ""}>
                      {r.manual_fallback_available ? "Available" : "—"}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">
                      {r.last_successful_connection ? String(r.last_successful_connection).slice(0, 16).replace("T", " ") : "—"}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{r.owner}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => testConn(r)} title="Test connection"
                          data-testid={`readiness-test-${r.id}`}
                          className="h-6 px-1.5 rounded border border-slate-200 text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-[10px]">
                          <PlugZap className="h-3 w-3" /> Test
                        </button>
                        {r.simulation_available && (
                          <button onClick={() => setSimDialog(r)} title="Run a labelled simulation"
                            data-testid={`readiness-sim-${r.id}`}
                            className="h-6 px-1.5 rounded border border-sky-200 text-sky-700 hover:bg-sky-50 inline-flex items-center gap-1 text-[10px]">
                            <FlaskConical className="h-3 w-3" /> Simulate
                          </button>
                        )}
                        <button onClick={() => setReqDialog(r)} title="View requirements"
                          data-testid={`readiness-req-${r.id}`}
                          className="h-6 px-1.5 rounded border border-slate-200 text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-[10px]">
                          <FileQuestion className="h-3 w-3" /> Req
                        </button>
                        {r.configure_path && (
                          <Link to={r.configure_path} title="Configure"
                            data-testid={`readiness-config-${r.id}`}
                            className="h-6 px-1.5 rounded border border-slate-200 text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-[10px]">
                            <Settings2 className="h-3 w-3" /> Configure
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {reqDialog && (
        <Dialog open onOpenChange={() => setReqDialog(null)}>
          <DialogContent className="sm:max-w-md" data-testid="readiness-req-dialog">
            <DialogHeader>
              <DialogTitle className="text-base">{reqDialog.service} — requirements</DialogTitle>
              <DialogDescription className="text-[12px]">{MODE_LABEL[reqDialog.access_mode]}</DialogDescription>
            </DialogHeader>
            <div className="text-[12px] text-slate-600 space-y-2">
              <p>{reqDialog.requirements}</p>
              {reqDialog.fallback_note && (
                <p className="text-indigo-700 bg-indigo-50 border border-indigo-100 rounded p-2 text-[11px]">
                  Available today: {reqDialog.fallback_note}
                </p>
              )}
              <p className="text-[10px] text-slate-400">
                Credentials are entered only in secure server-side configuration — never in the browser, URLs or documents.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {simDialog && <SimulateDialog r={simDialog} onClose={() => setSimDialog(null)} />}
    </div>
  );
}

const SimulateDialog = ({ r, onClose }) => {
  const [scenario, setScenario] = useState(r.scenarios?.[0] || "");
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/one-connect/readiness/${r.id}/simulate`, { scenario });
      setRun(data.run);
      toast.success(`Simulation ${data.run.simulation_run_id} completed — SIMULATED only`);
    } catch (e) { toast.error(e.response?.data?.detail || "Simulation failed"); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="readiness-sim-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Simulate — {r.service}</DialogTitle>
          <DialogDescription className="text-[12px]">
            Tests the ONE workflow end-to-end. Every event is labelled SIMULATED — no real external transaction happens.
          </DialogDescription>
        </DialogHeader>
        {!run ? (
          <>
            <select value={scenario} onChange={(e) => setScenario(e.target.value)}
              className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white"
              data-testid="readiness-sim-scenario">
              {(r.scenarios || []).map((s) => <option key={s}>{s}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
              <Button size="sm" onClick={go} disabled={busy || !scenario}
                className="bg-sky-700 hover:bg-sky-800" data-testid="readiness-sim-run">
                Run simulation
              </Button>
            </div>
          </>
        ) : (
          <div className="space-y-1.5" data-testid="readiness-sim-result">
            <div className="text-[11px] font-mono text-slate-500">
              {run.simulation_run_id} · by {run.executed_by} · {String(run.at).slice(0, 16).replace("T", " ")}
            </div>
            {run.events.map((ev) => (
              <div key={ev.seq} className="flex items-center gap-2 text-[12px]">
                <span className="rounded bg-sky-100 text-sky-800 px-1.5 py-0.5 text-[9px] font-bold">SIMULATED</span>
                <span className="text-slate-700">{ev.event}</span>
              </div>
            ))}
            <div className="flex justify-end pt-1">
              <Button size="sm" variant="outline" onClick={onClose}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
